import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { CheckedListControl } from "@/Common/Components/Inputs/CheckedListControl";
import { MultiDateSelector } from "@/Common/Components/Inputs/MultiDateSelector";
import { SearchSettingsFooter } from "@/Common/Components/Settings/SearchSettingsFooter";
import { CheckboxTarget } from "@/Common/Models/UI/Inputs/CheckboxTarget";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { ISalesInboxModel } from "../../Models/UI/ISalesInboxModel";

import "./SalesSettingsContainer.css";

interface ISalesSettingsContainerProps {
    Model: ISalesInboxModel;
    onClick?: (e: React.MouseEvent) => void;
    id?: string;
    className?: string;
    style?: React.CSSProperties;
}

class SalesSettingsContainer extends BaseReactComponent<ISalesSettingsContainerProps> {
    public componentDidMount(): void {
        super.setOverlay("SalesSettingsContainer", { transparent: true });
    }

    public render(): JSX.Element {
        return (
            <div id={this.props.id} className={this.props.className} style={this.props.style} onClick={this.props.onClick}>
                <table className="f-content">
                    <thead>
                        <tr>
                            <th>
                                <label style={{ display: "inline" }}>{cacheStore.GetString("DateLabel")}</label>
                            </th>
                            {this.props.Model.get("Sites").count() > 1 && (
                                <th>
                                    <CheckboxControl id="sites_check" label={cacheStore.GetString("SitesLabel")} operation={LocalOperations.Billing.Sales_OnSettingsContainerCheckboxChange} checked={this.props.Model.get("SelectAllSitesChecked")} target={CheckboxTarget.Myle_Billing_Sales_SitesControlCheck} />
                                </th>
                            )}
                            <th style={{ width: "200px" }}>
                                <CheckboxControl id="provider_check" label={cacheStore.GetString("ProviderLabel")} operation={LocalOperations.Billing.Sales_OnSettingsContainerCheckboxChange} checked={this.props.Model.get("SelectAllProviderChecked")} target={CheckboxTarget.Myle_Billing_Sales_ProviderControlCheck} />
                            </th>
                            <th>
                                <CheckboxControl id="billedby_check" label={cacheStore.GetString("BilledByLabel")} operation={LocalOperations.Billing.Sales_OnSettingsContainerCheckboxChange} checked={this.props.Model.get("SelectAllBilledByChecked")} target={CheckboxTarget.Myle_Billing_Sales_BilledByControlCheck} />
                            </th>
                            <th>
                                <CheckboxControl id="payment_check" label={cacheStore.GetString("PaymentLabel")} operation={LocalOperations.Billing.Sales_OnSettingsContainerCheckboxChange} checked={this.props.Model.get("SelectAllPaymentChecked")} target={CheckboxTarget.Myle_Billing_Sales_PaymentsControlCheck} />
                            </th>
                            <th>
                                <CheckboxControl id="status_check" label={cacheStore.GetString("StatusLabel")} operation={LocalOperations.Billing.Sales_OnSettingsContainerCheckboxChange} checked={this.props.Model.get("SelectAllStatusChecked")} target={CheckboxTarget.Myle_Billing_Sales_StatusControlCheck} />
                            </th>
                            <th>
                                <CheckboxControl id="validity_check" label={cacheStore.GetString("ValidityLabel")} operation={LocalOperations.Billing.Sales_OnSettingsContainerCheckboxChange} checked={this.props.Model.get("SelectAllValidityChecked")} target={CheckboxTarget.Myle_Billing_Settings_ValidityAllChecked} />
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>
                                <div>
                                    <MultiDateSelector
                                        label={cacheStore.GetString("CreationDateLabel")}
                                        id="doc_search_date_selector"
                                        fromMyleOperation={LocalOperations.Billing.Sales_SearchSettings_FromDateChanged}
                                        toMyleOperation={LocalOperations.Billing.Sales_SearchSettings_ToDateChanged}
                                        fromSetTodayLabel={cacheStore.GetString("TodayLabel")}
                                        toSetTodayLabel={cacheStore.GetString("TodayLabel")}
                                        from={this.props.Model.get("DateFrom")}
                                        to={this.props.Model.get("DateTo")}
                                        errorMessage={this.props.Model.get("ValidationError")}
                                    />
                                    <MultiDateSelector
                                        label={cacheStore.GetString("VisitDateLabel")}
                                        id="doc_search_visit_date_selector"
                                        fromMyleOperation={LocalOperations.Billing.Sales_SearchSettings_VisitFromDateChanged}
                                        toMyleOperation={LocalOperations.Billing.Sales_SearchSettings_VisitToDateChanged}
                                        fromSetTodayLabel={cacheStore.GetString("TodayLabel")}
                                        toSetTodayLabel={cacheStore.GetString("TodayLabel")}
                                        from={this.props.Model.get("VisitFromDate")}
                                        to={this.props.Model.get("VisitToDate")}
                                    />
                                </div>
                            </td>
                            {this.props.Model.get("Sites").count() > 1 && (
                                <td className="sites-column">
                                    <div style={{ maxHeight: "400px", minHeight: "400px", overflow: "auto" }}>
                                        <CheckedListControl
                                            DataSource={this.props.Model.get("Sites")}
                                            Operation={LocalOperations.Billing.Sales_OnSettingsContainerCheckboxChange}
                                            MaxTextLength={25}
                                        />

                                        <CheckboxControl
                                            id="sites_onlySelected"
                                            label={cacheStore.GetString("ShowOnlySelectedSiteLabel").toUpperCase()}
                                            operation={LocalOperations.Billing.Sales_OnSettingsContainerCheckboxChange}
                                            checked={this.props.Model.get("ShowOnlySelectedSites")}
                                            target={CheckboxTarget.Myle_Billing_Sales_OnlySelectedSites}
                                            DivWrap={true}
                                        />
                                    </div>
                                </td>
                            )}
                            <td>
                                <input className="form-control" style={{ width: "225px" }} type="text" value={this.props.Model.get("ProviderSearchText")} onChange={e => this.onProviderFilterTextChanged(e)} placeholder={cacheStore.GetString("SearchLabel").toUpperCase()} />
                                <div style={{ maxHeight: "400px", overflow: "auto" }}>
                                    <CheckedListControl idModifier="provider" DataSource={this.props.Model.get("ProviderFiltered")} Operation={LocalOperations.Billing.Sales_OnSettingsContainerCheckboxChange} sort={true} limitTextWidth={true} />
                                </div>
                            </td>
                            <td>
                                <input className="form-control" style={{ width: "225px" }} type="text" value={this.props.Model.get("BilledBySearchText")} onChange={e => this.onBilledByFilterTextChanged(e)} placeholder={cacheStore.GetString("SearchLabel").toUpperCase()} />
                                <div style={{ maxHeight: "400px", overflow: "auto" }}>
                                    <CheckedListControl idModifier="billedby" DataSource={this.props.Model.get("BilledByFiltered")} Operation={LocalOperations.Billing.Sales_OnSettingsContainerCheckboxChange} sort={true} limitTextWidth={true} />
                                </div>
                            </td>
                            <td>
                                <CheckedListControl idModifier="payment" DataSource={this.props.Model.get("PaymentMethod")} Operation={LocalOperations.Billing.Sales_OnSettingsContainerCheckboxChange} />
                            </td>
                            <td>
                                <CheckedListControl idModifier="status" DataSource={this.props.Model.get("Status")} Operation={LocalOperations.Billing.Sales_OnSettingsContainerCheckboxChange} />
                            </td>
                            <td>
                                <CheckedListControl idModifier="validity" DataSource={this.props.Model.get("Validity")} Operation={LocalOperations.Billing.Sales_OnSettingsContainerCheckboxChange} />
                            </td>
                        </tr>
                    </tbody>
                </table>
                <SearchSettingsFooter
                    onClose={() => sessionStore.dispatch(LocalOperations.Billing.Sales_SearchSettings_CancelClick)}
                    onOk={() => sessionStore.dispatch(LocalOperations.Billing.Sales_SearchSettings_OkClick)}
                    onColumnOrderingButtonClick={() => sessionStore.dispatch(LocalOperations.Billing.Sales_SearchSettings_ColumnOrderingPopup_Open)}
                    onResultsPerPageChange={value => sessionStore.dispatch(LocalOperations.Billing.Sales_SearchSettings_OnResultsPerPageChange, { value })}
                    selectedPageSizeOption={this.props.Model.get("SelectedPageSizeOption")}
                    pageSizeOptions={this.props.Model.get("PageSizeOptions").toArray()}
                    disableOkButton={this.getIsFormInvalid()}
                />
            </div>
        );
    }

    private getIsFormInvalid(): boolean {
        return !!this.props.Model.get("ValidationError");
    }

    public onProviderFilterTextChanged(event: React.ChangeEvent<HTMLInputElement>): void {
        var operation = new LocalEvent(LocalOperations.Billing.Sales_SearchSettings_ProviderFilterTextChanged);
        operation.AddItem("value", event.target.value);
        LocalEventDispatcher.dispatch(operation);
    }

    public onBilledByFilterTextChanged(event: React.ChangeEvent<HTMLInputElement>): void {
        var operation = new LocalEvent(LocalOperations.Billing.Sales_SearchSettings_BilledByFilterTextChanged);
        operation.AddItem("value", event.target.value);
        LocalEventDispatcher.dispatch(operation);
    }
}

export { SalesSettingsContainer };
